import { Handle, Position, type NodeProps } from "@xyflow/react";
import { GitBranch } from "lucide-react";
import type { WorkflowConditionChoice } from "@proclaw/shared";
import { cn } from "@/lib/utils";
import {
  runNodeBadgeClasses,
  runNodeBadgeLabel,
  runNodeDotClasses,
  runNodeFrameClasses,
  runNodeGlowClasses,
} from "@/components/workflows/node-theme";

/** Condition node with either two binary outputs or named choice outputs. */
export function ConditionNode({ data, selected }: NodeProps) {
  const d = data as {
    label?: string;
    expression?: string;
    description?: string;
    mode?: "binary" | "multiple";
    choices?: WorkflowConditionChoice[];
    runStatus?: string;
    runSelected?: boolean;
  };
  const multiple = d.mode === "multiple";
  let pathNumber = 0;
  const outputs: WorkflowConditionChoice[] = multiple
    ? (d.choices ?? []).map((choice) => ({
        ...choice,
        label: choice.isDefault ? "Otherwise" : `Path ${++pathNumber}`,
      }))
    : [
        { id: "yes", label: "Yes" },
        { id: "no", label: "No" },
      ];
  const runFrame = runNodeFrameClasses(d.runStatus);
  const runDot = runNodeDotClasses(d.runStatus);
  const runGlow = runNodeGlowClasses(d.runStatus);
  const runBadge = runNodeBadgeLabel(d.runStatus);
  const runBadgeClasses = runNodeBadgeClasses(d.runStatus);

  return (
    <div
      className={cn(
        "relative w-[260px] rounded-xl border bg-surface shadow-sm transition-all duration-300",
        runFrame ?? (selected ? "border-amber-500 shadow-amber-500/20" : "border-border"),
        d.runSelected && "outline outline-2 outline-offset-4 outline-accent/70",
      )}
    >
      {runGlow && (
        <span className={cn("pointer-events-none absolute -inset-3 z-0 rounded-[1.35rem]", runGlow)} />
      )}
      {runDot && (
        <span className={cn("absolute right-2 top-2 z-10 h-2.5 w-2.5 rounded-full ring-2 ring-surface", runDot)} />
      )}
      {runBadge && runBadgeClasses && (
        <span className={cn("absolute -right-2 -top-3 z-20 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide", runBadgeClasses)}>
          {runBadge}
        </span>
      )}

      <div className="flex items-center gap-1.5 bg-amber-500/10 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-amber-600">
        <GitBranch size={11} />
        Condition
      </div>
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600">
          <GitBranch size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-text-primary">
            {d.label || "Condition"}
          </p>
          <p className="mt-0.5 line-clamp-2 text-[11px] text-text-muted">
            {multiple
              ? `${outputs.length} possible outcomes`
              : d.expression || d.description || "e.g. score > 70"}
          </p>
        </div>
      </div>

      <Handle
        type="target"
        position={Position.Left}
        className="!h-2.5 !w-2.5 !border-2 !border-surface !bg-amber-500"
      />

      <div className="border-t border-border/70 px-3 py-1.5">
        {outputs.map((output) => (
          <div
            key={output.id}
            className="relative flex h-7 items-center justify-end pr-2 text-[10px] font-medium text-text-muted"
          >
            <span className="max-w-[180px] truncate">
              {output.label}
            </span>
            <Handle
              id={output.id}
              type="source"
              position={Position.Right}
              className={cn(
                "!h-2.5 !w-2.5 !border-2 !border-surface",
                !multiple && output.id === "yes"
                  ? "!bg-green-500"
                  : !multiple && output.id === "no"
                    ? "!bg-red-500"
                    : "!bg-amber-500",
              )}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
